import { Badge } from "@/components/ui/badge";
import { useTranslation } from "@/hooks/useTranslation";
import { DebugLoading, DebugQuote } from "./debug-tab-parts";
import { formatBounds } from "./format";
import type { Incident } from "./incidents";

type IncidentsTabProps = {
  incidents: Incident[];
  isLoading: boolean;
};

/** The turns that were cut, stopped or errored. */
export function IncidentsTab({ incidents, isLoading }: IncidentsTabProps) {
  const { t } = useTranslation();

  if (isLoading) return <DebugLoading />;

  if (incidents.length === 0) {
    return (
      <p className="text-muted-foreground text-sm">
        {t("chat.debug.incidents.none")}
      </p>
    );
  }

  const hasSessionOnly = incidents.some(
    (incident) => incident.scope === "session"
  );

  return (
    <div className="space-y-3">
      {incidents.map((incident) => (
        <IncidentCard incident={incident} key={incident.key} />
      ))}
      {hasSessionOnly && (
        <p className="text-muted-foreground text-xs">
          {t("chat.debug.incidents.sessionNote")}
        </p>
      )}
    </div>
  );
}

function IncidentCard({ incident }: { incident: Incident }) {
  const { t } = useTranslation();

  return (
    <div className="space-y-1.5 rounded-md border p-3">
      <div className="flex items-start justify-between gap-3">
        <span className="font-medium text-sm">
          {t(incident.labelKey, incident.labelValues)}
        </span>
        <Badge
          variant={incident.scope === "persisted" ? "outline" : "secondary"}
        >
          {incident.scope === "persisted"
            ? t("chat.debug.incidents.scopePersisted")
            : t("chat.debug.incidents.scopeSession")}
        </Badge>
      </div>
      <p className="font-mono text-muted-foreground text-xs">
        {incident.at} · {incident.messageId}
      </p>
      <DebugQuote
        text={formatBounds(incident.contentStart, incident.contentEnd)}
      />
      {incident.detail && <p className="text-sm">{incident.detail}</p>}
      {incident.cause && (
        <p className="font-mono text-muted-foreground text-xs">
          {t("chat.debug.incidents.cause", { cause: incident.cause })}
        </p>
      )}
      {incident.retryable !== null && (
        <Badge variant={incident.retryable ? "secondary" : "destructive"}>
          {incident.retryable
            ? t("chat.debug.incidents.retryable")
            : t("chat.debug.incidents.notRetryable")}
        </Badge>
      )}
    </div>
  );
}
